import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { routingService } from "../services/routing.service";

export const routingKeys = {
  rules: ["routing", "rules"],
  capabilities: ["routing", "capabilities"],
};

export function useRoutingRules() {
  const queryClient = useQueryClient();

  const rulesQuery = useQuery({
    queryKey: routingKeys.rules,
    queryFn: routingService.listRules,
  });

  const capabilitiesQuery = useQuery({
    queryKey: routingKeys.capabilities,
    queryFn: routingService.listCapabilities,
  });

  const createRuleMutation = useMutation({
    mutationFn: (payload) => routingService.createRule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: routingKeys.rules,
      });

      // Also refresh the admin list because AdminSettings
      // displays the admin endpoint's copy of the rules.
      queryClient.invalidateQueries({
        queryKey: ["admin", "routing-rules"],
      });
    },
  });

  const updateRuleMutation = useMutation({
    mutationFn: ({ ruleId, payload }) =>
      routingService.updateRule(ruleId, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: routingKeys.rules,
      });

      queryClient.invalidateQueries({
        queryKey: ["admin", "routing-rules"],
      });
    },
  });

  const createCapabilityMutation = useMutation({
    mutationFn: (payload) =>
      routingService.createCapability(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: routingKeys.capabilities,
      });

      queryClient.invalidateQueries({
        queryKey: ["admin", "provider-capabilities"],
      });
    },
  });

  const updateCapabilityMutation = useMutation({
    mutationFn: ({ capabilityId, payload }) =>
      routingService.updateCapability(capabilityId, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: routingKeys.capabilities,
      });

      queryClient.invalidateQueries({
        queryKey: ["admin", "provider-capabilities"],
      });
    },
  });

  const selectRouteMutation = useMutation({
    mutationFn: (payload) =>
      routingService.selectRoute(payload),
  });

  const createRoutingDecisionMutation = useMutation({
    mutationFn: ({ transferId, payload }) =>
      routingService.createRoutingDecision(
        transferId,
        payload
      ),
  });

  return {
    // Queries
    rules: rulesQuery.data ?? [],
    capabilities: capabilitiesQuery.data ?? [],

    isLoadingRules: rulesQuery.isLoading,
    isLoadingCapabilities: capabilitiesQuery.isLoading,

    rulesError: rulesQuery.error,
    capabilitiesError: capabilitiesQuery.error,

    // Rule mutations
    createRule: createRuleMutation.mutateAsync,
    isCreatingRule: createRuleMutation.isPending,

    updateRule: updateRuleMutation.mutateAsync,
    isUpdatingRule: updateRuleMutation.isPending,

    // Capability mutations
    createCapability: createCapabilityMutation.mutateAsync,
    isCreatingCapability: createCapabilityMutation.isPending,

    updateCapability: updateCapabilityMutation.mutateAsync,
    isUpdatingCapability: updateCapabilityMutation.isPending,

    // Routing operations
    selectRoute: selectRouteMutation.mutateAsync,
    isSelectingRoute: selectRouteMutation.isPending,

    createRoutingDecision:
      createRoutingDecisionMutation.mutateAsync,
    isCreatingRoutingDecision:
      createRoutingDecisionMutation.isPending,
  };
}